import { createContext, useContext, useState, useRef, useCallback } from 'react';

/**
 * CMSContext structure:
 *
 * dirty: { [sectionId]: true }
 * saveHandlers: Map<sectionId, () => Promise>
 * lastSavedAt: timestamp
 *
 * setDirty(sectionId, boolean)
 * registerSaveHandler(sectionId, fn)
 * unregisterSaveHandler(sectionId)
 * runAllSaveHandlers()
 * clearDirty()
 * discardAll()
 * setLastSavedAt(timestamp)
 */

const CMSContext = createContext(null);

export const CMSProvider = ({ children }) => {
  /* ---------------------------------------------------
   * STATE
   * --------------------------------------------------- */
  const [dirty, setDirtyState] = useState({}); // { hero: true, footer: true }
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // saveHandlers is a Map but stored in a ref to avoid re-renders
  // Each handler: () => Promise
  const saveHandlers = useRef(new Map());

  /* ---------------------------------------------------
   * DIRTY MANAGEMENT
   * --------------------------------------------------- */
  const setDirty = useCallback((sectionId, isDirty) => {
    setDirtyState((prev) => {
      const updated = { ...prev };
      if (isDirty) updated[sectionId] = true;
      else delete updated[sectionId];
      return updated;
    });
  }, []);

  const clearDirty = useCallback(() => {
    setDirtyState({});
  }, []);

  const discardAll = useCallback(() => {
    // You can extend this logic to reset local editor state by broadcasting an event.
    // For now, this only clears dirty flags so CommandBar disappears.
    setDirtyState({});
  }, []);

  /* ---------------------------------------------------
   * SAVE HANDLER REGISTRATION
   * --------------------------------------------------- */
  const registerSaveHandler = useCallback((sectionId, fn) => {
    saveHandlers.current.set(sectionId, fn);
  }, []);

  const unregisterSaveHandler = useCallback((sectionId) => {
    saveHandlers.current.delete(sectionId);
  }, []);

  /* ---------------------------------------------------
   * RUN ALL SAVE HANDLERS
   * --------------------------------------------------- */
  const runAllSaveHandlers = useCallback(async () => {
    const handlers = Array.from(saveHandlers.current.values());

    if (handlers.length === 0) return true;

    // Run sequentially but safely
    for (const handler of handlers) {
      if (typeof handler === 'function') {
        await handler(); // handler() returns a Promise in all editors
      }
    }

    setLastSavedAt(Date.now());
    clearDirty();

    return true;
  }, [clearDirty]);

  /* ---------------------------------------------------
   * VALUE
   * --------------------------------------------------- */
  const value = {
    dirty,
    lastSavedAt,

    setDirty,
    clearDirty,
    discardAll,

    registerSaveHandler,
    unregisterSaveHandler,
    runAllSaveHandlers,

    setLastSavedAt,
  };

  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
};

/* ---------------------------------------------------
 * USE CMS HOOK
 * --------------------------------------------------- */
export const useCMS = () => {
  const ctx = useContext(CMSContext);
  if (!ctx) {
    throw new Error('useCMS must be used inside <CMSProvider>');
  }
  return ctx;
};
